import React, { useRef } from "react";
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  Spacer,
  Text,
  useBoolean,
  VStack
} from "@chakra-ui/react";
import { format } from "date-fns";
import { IoTrashBinOutline } from "react-icons/io5";
import { TextInput, Form } from "components";
import { FormInstance, useAuth, useOrganizationSettings } from "duck";
import { Fragments } from "requests";
import { CommentLocation } from "../../duck";
import { MarkButton, CommentText } from "./components";
import {
  FormValues,
  SCROLLBAR_STYLES,
  useCreateDentalSketchComment,
  useDeleteDentalSketchComment,
  useUpdateDentalSketchComment,
  VALIDATION_SCHEMA
} from "./duck";

interface CommentProps {
  comment?: Omit<
    Fragments.StoredDentalSketchComments[number],
    "locationX" | "locationY"
  >;
  location: CommentLocation;
  isNew?: boolean;
  onSave?(): void;
}

const Comment: React.FC<CommentProps> = ({
  comment,
  location,
  isNew = false,
  onSave
}) => {
  const [isEditing, setIsEditing] = useBoolean(!comment);

  const formRef = useRef<FormInstance<FormValues>>(null);

  const [{ user }] = useAuth();
  const author = comment?.createdBy || user;
  const isSameUser = author._id === user._id;
  const fullName = `${author.firstName} ${author.lastName}`;
  const { dateFormat } = useOrganizationSettings();

  const { create, loading: createLoading } = useCreateDentalSketchComment(
    location,
    user
  );
  const { update, loading: updateLoading } = useUpdateDentalSketchComment(
    comment?._id || comment?.fakeId
  );
  const {
    deleteComment,
    loading: deleteCommentLoading
  } = useDeleteDentalSketchComment();

  const saveHandler = () => {
    formRef.current?.submit().then(() => {
      if (onSave) {
        onSave();
      }
      setIsEditing.off();
    });
  };

  const closeHandler = () => {
    if (isEditing && comment) {
      setIsEditing.off();
    }
  };

  return (
    <Box
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
      }}
      position="absolute"
      left={`${location.x}%`}
      top={`${location.y}%`}
    >
      <Popover onClose={closeHandler} placement="right" defaultIsOpen={isNew}>
        {({ isOpen }) => (
          <>
            <MarkButton isOpen={isOpen} author={author} />
            {isOpen && (
              <PopoverContent>
                <PopoverBody>
                  <VStack alignItems="left">
                    <HStack justifyContent="space-between">
                      <Text fontWeight="semibold" fontSize="sm">
                        {fullName}
                      </Text>
                      {comment?.createdAt && (
                        <Text color="gray.600" fontSize="sm">
                          {format(
                            new Date(comment.createdAt),
                            `${dateFormat}, h:mm aa`
                          )}
                        </Text>
                      )}
                    </HStack>

                    {isEditing ? (
                      <Form
                        innerRef={formRef}
                        onSubmit={comment ? update : create}
                        defaultValues={{ text: comment?.text || "" }}
                        validationSchema={VALIDATION_SCHEMA}
                      >
                        <TextInput
                          type="text-area"
                          name="text"
                          sx={{
                            ...SCROLLBAR_STYLES,
                            "&::-webkit-scrollbar-corner": {
                              display: "none"
                            }
                          }}
                        />
                      </Form>
                    ) : (
                      <CommentText text={comment?.text} />
                    )}

                    {isSameUser && (
                      <HStack>
                        {comment && (
                          <Button
                            onClick={() =>
                              deleteComment(
                                comment?._id ||
                                  (comment?.fakeId as string | number)
                              )
                            }
                            colorScheme="red"
                            size="sm"
                            isLoading={deleteCommentLoading}
                          >
                            <IoTrashBinOutline />
                          </Button>
                        )}
                        <Spacer />
                        {comment && (
                          <Button
                            colorScheme="minor"
                            size="sm"
                            onClick={() => {
                              setIsEditing.toggle();
                            }}
                          >
                            {isEditing ? "Cancel" : "Edit"}
                          </Button>
                        )}
                        {isEditing && (
                          <Button
                            size="sm"
                            isLoading={createLoading || updateLoading}
                            onClick={saveHandler}
                          >
                            Save
                          </Button>
                        )}
                      </HStack>
                    )}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            )}
          </>
        )}
      </Popover>
    </Box>
  );
};

export default Comment;
